import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import FacebookFeed from '../components/FacebookFeed'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

const HERO_SLIDES = [
  {
    image: 'img-7698-min-scaled.jpg',
    alt: 'EK Biler autoværksted i Hejnsvig',
  },
  {
    image: 'https://assets.auxodev.dk/assets/a2f45633-eb66-4ac0-a380-9125c1343795/imgi-2-billede-1-1.jpg',
    alt: 'EK Biler værksted og bilsalg facade',
  },
]

export default function HomePage({ settings }) {
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % HERO_SLIDES.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [])

  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'
  const email = settings?.email || 'steen@ekbiler.dk'
  const address = settings?.address || 'Mejerivej 12, 7250 Hejnsvig'
  const facebookUrl = settings?.facebook_url || 'https://www.facebook.com/ekbiler.dk/'
  const mapsUrl =
    settings?.maps_url ||
    `https://www.google.com/maps/search/?api=1&query=EK+Biler+${encodeURIComponent(address)}`

  return (
    <>
      <MetaTags
        title="Autoværksted i Hejnsvig. Vi har fokus på service & kvalitet. Ring nu"
        description="Leder du efter en autoværksted i Hejnsvig? Vi udfører service & reparation af biler. Kontakt os i dag og få et uforpligtende tilbud."
        ogTitle="Autoværksted i Hejnsvig. Vi har fokus på service & kvalitet. Ring nu"
        ogDescription="Leder du efter en autoværksted i Hejnsvig? Vi udfører service & reparation af biler. Kontakt os i dag og få et uforpligtende tilbud."
        ogImage={imageUrl('billede-1-1.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* 1. HERO SECTION (Matching Motorcycle Repair Workshop Showcase Layout) */}
      <section className="relative bg-[#14171a] text-white overflow-hidden -mt-20 pt-40 pb-28 sm:pt-48 sm:pb-32 lg:pt-52 lg:pb-36">
        {/* Full-bleed atmospheric workshop background with image slider */}
        <div className="absolute inset-0 z-0">
          {HERO_SLIDES.map((slide, index) => (
            <img
              key={index}
              src={imageUrl(slide.image, 'width=1920,quality=85,format=auto')}
              alt={slide.alt}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ease-in-out filter brightness-90 contrast-105 ${
                index === currentHeroSlide ? 'opacity-70' : 'opacity-0 pointer-events-none'
              }`}
            />
          ))}
          {/* Lighter, more transparent gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#14171a]/50 via-[#14171a]/30 to-[#14171a]/65" />
        </div>

        {/* Central Vertical Content Stack (exact layout as Motorcycle Repair hero) */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 flex flex-col items-center justify-center text-center">
          {/* Overline / Subtitle */}
          <div className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase text-zinc-300 mb-3">
            VELKOMMEN TIL EK BILER • HEJNSVIG
          </div>

          {/* High-Contrast Condensed Uppercase H1 (font-light vs font-extrabold) */}
          <h1 className="font-display uppercase tracking-wider text-white text-4xl sm:text-6xl md:text-7xl lg:text-8xl leading-tight sm:leading-none my-2 drop-shadow-sm">
            <span className="font-light tracking-widest text-white/95">DIT LOKALE </span>
            <span className="font-extrabold tracking-wider text-white">AUTOVÆRKSTED</span>
          </h1>

          <div className="text-sm sm:text-base tracking-[0.25em] font-medium text-zinc-400 uppercase mt-2">
            Autoværksted &amp; Bilforhandler • Hejnsvig
          </div>

          {/* Description Copy */}
          <p className="mt-6 text-sm sm:text-base md:text-lg text-zinc-300 leading-relaxed max-w-2xl mx-auto font-normal">
            EK Biler hjælper med service, reparation og vedligeholdelse af din bil. Hos os finder du
            desuden et stort udvalg af brugte biler til salg fra forskellige mærker. Vi lægger vægt på
            ordentligt arbejde, god service og en enkel proces fra booking til aflevering.
          </p>

          {/* Quick Strengths Pill Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-6 text-xs text-zinc-300">
            <span className="px-3 py-1 bg-white/10 backdrop-blur-xs border border-white/15">
              Speciale i V.A.G Gruppen
            </span>
            <span className="px-3 py-1 bg-white/10 backdrop-blur-xs border border-white/15">
              Altid gratis lånebil
            </span>
            <span className="px-3 py-1 bg-white/10 backdrop-blur-xs border border-white/15">
              6 autolifte &amp; moderne testudstyr
            </span>
            <span className="px-3 py-1 bg-white/10 backdrop-blur-xs border border-white/15">
              El- og hybridbiler
            </span>
          </div>

          {/* Primary CTA Buttons (sharp industrial rectangular styling) */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full sm:w-auto">
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-9 py-3.5 rounded-none font-bold text-xs sm:text-sm uppercase tracking-[0.2em] shadow-lg hover:shadow-red-900/40 transition-all text-center focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book tid</span>
            </a>

            <a
              href={`tel:${phoneRaw}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white px-7 py-3.5 rounded-none border border-white/30 font-bold text-xs sm:text-sm uppercase tracking-[0.15em] transition-colors text-center focus:outline-none focus:ring-2 focus:ring-white"
            >
              <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
              <span>Ring {phone}</span>
            </a>

            <Link
              to="/brugte-biler"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-zinc-300 hover:text-white px-5 py-3.5 font-bold text-xs sm:text-sm uppercase tracking-[0.15em] transition-colors text-center"
            >
              <span>Se brugte biler</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Social Icons Bar (Matching Motorcycle Repair Hero) */}
          <div className="flex items-center justify-center gap-5 text-white/80 mt-8 text-base sm:text-lg">
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="EK Biler på Facebook"
              className="hover:text-white hover:scale-110 transition-all p-1"
            >
              <i className="fa-brands fa-facebook-f" aria-hidden="true" />
            </a>
            <a
              href={`tel:${phoneRaw}`}
              aria-label="Ring til os"
              className="hover:text-white hover:scale-110 transition-all p-1"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
            </a>
            <a
              href={`mailto:${email}`}
              aria-label="Send e-mail"
              className="hover:text-white hover:scale-110 transition-all p-1"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
            </a>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Find vej på Google Maps"
              className="hover:text-white hover:scale-110 transition-all p-1"
            >
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Bottom Pointer Divider: Centered upward triangular notch intersecting the boundary */}
        <div
          className="absolute -bottom-[1px] left-1/2 -translate-x-1/2 w-0 h-0 border-x-[24px] border-x-transparent border-b-[20px] border-b-white z-20 pointer-events-none"
          aria-hidden="true"
        />
      </section>

      {/* 2. "ABOUT US" CONTENT SECTION (Matching Split Layout in Screenshot 1) */}
      <section className="bg-white py-20 lg:py-28 px-4 sm:px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left Column: Tall Vertical Workshop/Mechanic Portrait Frame */}
            <div className="lg:col-span-5 relative">
              <div className="overflow-hidden bg-zinc-900 border border-zinc-200 shadow-md">
                <img
                  src={imageUrl('shutterstock-2258342797-optimized-1.jpg', 'width=800,quality=85,format=auto')}
                  alt="Mekaniker hos EK Biler"
                  className="w-full h-[420px] sm:h-[480px] lg:h-[530px] object-cover object-center filter contrast-105"
                  loading="lazy"
                />
              </div>

              {/* Decorative industrial corner accent */}
              <div className="hidden sm:block absolute -bottom-4 -left-4 w-24 h-24 border-b-2 border-l-2 border-[#e20019]/40 pointer-events-none" />
            </div>

            {/* Right Column: Editorial Text Stack */}
            <div className="lg:col-span-7 space-y-5">
              {/* Kicker */}
              <div className="text-xs font-semibold tracking-[0.25em] text-zinc-400 uppercase">
                OM OS • EK BILER
              </div>

              {/* H2 in Brand Tone matching condensed uppercase header in Screenshot 1 */}
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide text-[#e20019] uppercase leading-tight">
                Hvorfor vælge EK Biler som dit autoværksted?
              </h2>

              {/* Body Text */}
              <p className="text-zinc-600 text-sm sm:text-base leading-relaxed">
                Med vores kombination af bilsalg, bilservice og reparationer får du en
                samarbejdspartner, der kan hjælpe dig i hele bilens levetid. Vi prioriterer tryghed og
                høj faglighed – og vi arbejder hver dag på at være det lokale værksted, som både nye og
                faste kunder gerne vender tilbage til.
              </p>

              {/* Core Points list */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-red-100 text-[#e20019] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      Autoværksted til alle bilmærker – med speciale i V.A.G-gruppen
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Vores mekanikere har særlig specialviden inden for VW, Audi, Seat og Skoda, samt
                      dybdegående erfaring med alle øvrige mærker og modeller.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-red-100 text-[#e20019] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      Fleksibilitet og gratis lånebil
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Når du afleverer din bil hos os, har du altid mulighed for at få en lånebil, så din
                      dag kan fortsætte uden afbrydelser.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-red-100 text-[#e20019] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      Gennemskuelige priser &amp; ærlig rådgivning
                    </h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Vi tager 595 kr. ekskl. moms i timen. Vi gennemgår altid arbejdet med dig, inden
                      vi går i gang – så du aldrig får overraskelser på regningen.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                <a
                  href={`tel:${phoneRaw}`}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-900 px-6 py-3 rounded-none text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors text-center"
                >
                  <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                  <span>Ring {phone}</span>
                </a>

                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-none text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors text-center"
                >
                  <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
                  <span>Book tid</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. DECORATIVE GEOMETRIC TRANSITION / HALFTONE DOT MATRIX DIVIDER (Matching Screenshot 2) */}
      <section className="relative w-full overflow-hidden bg-zinc-50 border-y border-zinc-200 py-10">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, #71717a 1px, transparent 1px), radial-gradient(circle, #71717a 1px, transparent 1px)',
            backgroundSize: '24px 24px',
            backgroundPosition: '0 0, 12px 12px',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#e20019]">
              AUTORISERET SERVICE &amp; REPARATION
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold uppercase text-zinc-900 tracking-wide mt-1">
              Professionelt autoværksted for hele lokalområdet
            </h3>
            <p className="text-xs sm:text-sm text-zinc-500 mt-1 max-w-xl">
              Vi betjener kunder fra Hejnsvig, Billund, Grindsted og Vorbasse med hurtig service og høj kvalitet.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto justify-center lg:justify-end">
            <a
              href={`tel:${phoneRaw}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white border border-zinc-300 hover:border-zinc-400 text-zinc-900 px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all"
            >
              <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
              <span>{phone}</span>
            </a>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book tid online</span>
            </a>
          </div>
        </div>
      </section>

      {/* 4. WORKSHOP SERVICES SECTION (Sharp Industrial Grid Aesthetic) */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-block px-3 py-1 bg-zinc-100 border border-zinc-300 text-[#e20019] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              VORES YDELSER • AUTOVÆRKSTED I HEJNSVIG
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold uppercase text-gray-900 tracking-wide">
              Professionelt værksted med fokus på service og kvalitet
            </h2>
            <p className="mt-3 text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Leder du efter et autoværksted, der kan håndtere både hverdagens bilservice og
              avancerede reparationer, hjælper vi dig gerne – uanset om du bor i Hejnsvig, Billund,
              Grindsted eller Vorbasse.
            </p>
          </div>

          {/* Service Cards Grid (sharp rectangular styling) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1: Serviceeftersyn */}
            <div className="bg-white border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-gray-400 transition-all group">
              <div>
                <div className="aspect-[16/10] overflow-hidden bg-gray-200">
                  <img
                    src={imageUrl('shutterstock-2258342797-optimized-1.jpg', 'width=600,quality=80,format=auto')}
                    alt="udførsel af serviceeftersyn på bil"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mb-2 group-hover:text-[#e20019] transition-colors">
                    Serviceeftersyn
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Hos EK Biler nær Billund og Grindsted tilbyder vi professionelle serviceeftersyn til
                    alle typer biler, uanset mærke eller model. Både lille og stort serviceeftersyn med
                    bevaret garanti.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/serviceeftersyn"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#e20019] hover:text-[#b80014] transition-colors"
                >
                  <span>Læs mere om serviceeftersyn</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 2: Dækhotel */}
            <div className="bg-white border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-gray-400 transition-all group">
              <div>
                <div className="aspect-[16/10] overflow-hidden bg-gray-200">
                  <img
                    src={imageUrl('shutterstock-2307555301-optimized-1.jpg', 'width=600,quality=80,format=auto')}
                    alt="Dækhotel hos EK Biler"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mb-2 group-hover:text-[#e20019] transition-colors">
                    Dækhotel
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Dækhotel hos EK Biler. Oplever du også, at dine dæk fylder mere, end du har plads til
                    i garagen eller kælderen? Korrekte opbevaringsforhold forlænger dækkenes levetid.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/daekhotel"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#e20019] hover:text-[#b80014] transition-colors"
                >
                  <span>Læs mere om dækhotel</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 3: Dækskift */}
            <div className="bg-white border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-gray-400 transition-all group">
              <div>
                <div className="aspect-[16/10] overflow-hidden bg-gray-200">
                  <img
                    src={imageUrl('shutterstock-2416863723-optimized-1.jpg', 'width=600,quality=80,format=auto')}
                    alt="dækskifte på autoværksted"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mb-2 group-hover:text-[#e20019] transition-colors">
                    Dækskift
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Hos EK Biler varetager vi dækskift som en del af vores ydelser. Vi servicerer bilejere
                    fra Billund, Grindsted og omegn med omhyggelig montering og afbalancering.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/daekskift"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#e20019] hover:text-[#b80014] transition-colors"
                >
                  <span>Læs mere om dækskift</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 4: Elbil service */}
            <div className="bg-white border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-gray-400 transition-all group">
              <div>
                <div className="aspect-[16/10] overflow-hidden bg-gray-200">
                  <img
                    src={imageUrl('img-7701-min-scaled.jpg', 'width=600,quality=80,format=auto')}
                    alt="Service af elbiler og hybridbiler"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mb-2 group-hover:text-[#e20019] transition-colors">
                    Elbil service
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Vi arbejder dagligt med service af elbiler og hybridbiler, og vi sørger for, at din
                    bil får præcis den opmærksomhed, moderne elteknologi og højspænding kræver.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/elbil-service"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#e20019] hover:text-[#b80014] transition-colors"
                >
                  <span>Læs mere om elbil service</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 5: Montering af vinterdæk */}
            <div className="bg-white border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-gray-400 transition-all group">
              <div>
                <div className="aspect-[16/10] overflow-hidden bg-gray-200">
                  <img
                    src={imageUrl('img-7698-min-scaled.jpg', 'width=600,quality=80,format=auto')}
                    alt="Montering af vinterdæk"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mb-2 group-hover:text-[#e20019] transition-colors">
                    Montering af vinterdæk
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Få bilen gjort klar til de kolde måneder med faglig rådgivning og omhyggelig montering
                    af vinterdæk med fokus på maksimal sikkerhed på vejene.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/vinterdaek"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#e20019] hover:text-[#b80014] transition-colors"
                >
                  <span>Læs mere om vinterdæk</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Card 6: Bilsalg & Klargøring */}
            <div className="bg-white border border-gray-200 overflow-hidden flex flex-col justify-between hover:shadow-lg hover:border-gray-400 transition-all group">
              <div>
                <div className="aspect-[16/10] overflow-hidden bg-gray-200">
                  <img
                    src={imageUrl('billede-1-1.jpg', 'width=600,quality=80,format=auto')}
                    alt="Brugte biler hos EK Biler"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-gray-900 mb-2 group-hover:text-[#e20019] transition-colors">
                    Brugte biler til salg
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    EK Biler handler med gode, brugte kvalitetsbiler i alle størrelser og mærker. Alle
                    biler er grundigt gennemgået, testet og klargjort på vores eget værksted.
                  </p>
                </div>
              </div>
              <div className="p-6 pt-0">
                <Link
                  to="/brugte-biler"
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#e20019] hover:text-[#b80014] transition-colors"
                >
                  <span>Se aktuelle biler på lager</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. DIRECT WORKSHOP CONTACT & OPENING HOURS BLOCK (High-Contrast Industrial Showcase) */}
      <section className="bg-[#14171a] text-white py-16 lg:py-20 border-y border-[#282f37]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Workshop image & lift capacity */}
            <div className="lg:col-span-5 relative">
              <div className="border border-[#2d3540] overflow-hidden shadow-2xl">
                <img
                  src={imageUrl('ek-biler6.jpg', 'width=800,quality=85,format=auto')}
                  alt="EK Biler facade og værksted"
                  className="w-full h-[360px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white text-zinc-900 p-4 border border-zinc-200 shadow-xl hidden sm:flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 text-[#e20019] flex items-center justify-center text-xl font-black font-display">
                  6
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-900">Moderne Autolifte</div>
                  <div className="text-[11px] text-gray-500">Hurtig håndtering &amp; kort ventetid</div>
                </div>
              </div>
            </div>

            {/* Information & Opening hours details */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-[0.25em] mb-1">
                  DIREKTE KONTAKT &amp; ÅBNINGSTIDER
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase text-white tracking-wide">
                  EK Biler v/Steen Svensson Ek
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-950/80 border border-red-800 text-[#e20019] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs uppercase tracking-wider">Værksted og salg</div>
                      <div className="text-white font-semibold">{address}</div>
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#e20019] hover:underline text-xs font-bold mt-1"
                      >
                        <span>Find vej på Google Maps</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-950/80 border border-red-800 text-[#e20019] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs uppercase tracking-wider">Telefon</div>
                      <a
                        href={`tel:${phoneRaw}`}
                        className="text-white font-bold text-lg hover:text-[#e20019] transition-colors whitespace-nowrap block"
                      >
                        {phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-950/80 border border-red-800 text-[#e20019] flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-envelope text-sm" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs uppercase tracking-wider">E-mail</div>
                      <a
                        href={`mailto:${email}`}
                        className="text-white font-semibold hover:text-[#e20019] transition-colors whitespace-nowrap"
                      >
                        {email}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Hours Box */}
                <div className="bg-[#1c2127] border border-[#2d3540] p-5 text-xs space-y-2.5">
                  <div className="text-white font-bold uppercase tracking-wider flex items-center justify-between pb-1 border-b border-[#2d3540]">
                    <span>Værkstedets åbningstider:</span>
                    <span className="text-emerald-400 font-medium">Hverdage</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Mandag – Torsdag</span>
                    <span className="text-white font-bold">7:30 – 16:00</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Fredag</span>
                    <span className="text-white font-bold">7:30 – 13:30</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Lørdag – Søndag</span>
                    <span className="text-gray-500">Lukket</span>
                  </div>
                  <div className="pt-2 text-[11px] text-gray-400 border-t border-[#2d3540]">
                    * Bilsalg fremvises også gerne efter aftale.
                  </div>
                </div>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-7 py-3 rounded-none font-bold text-xs sm:text-sm uppercase tracking-wider shadow-md transition-all text-center whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Bestil tid online</span>
                </a>
                <a
                  href={`tel:${phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 bg-[#20252c] text-white px-6 py-3 rounded-none font-bold text-xs sm:text-sm uppercase tracking-wider transition-colors text-center whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                  <span>Ring på {phone}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. DIN BILPARTNER BADGE TRUST SECTION */}
      <DinBilpartnerBadge />

      {/* 7. FACEBOOK FEED SECTION */}
      <FacebookFeed facebookUrl={settings?.facebook_url} />

      {/* 8. CUSTOMER REVIEWS SECTION */}
      <ReviewsSlider />

      {/* 9. CONTACT FORM & SPLIT SECTION (Matching Design Reference "Webform on right") */}
      <section className="relative py-20 lg:py-28 bg-[#14171a] text-white overflow-hidden border-t border-[#262c33]">
        {/* Full-bleed atmospheric background image with more transparent overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl('billede-1-1.jpg', 'width=1920,quality=80,format=auto')}
            alt="EK Biler autoværksted"
            className="w-full h-full object-cover object-center opacity-65 filter brightness-85 contrast-115"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121518]/75 via-[#121518]/60 to-[#121518]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          {/* Split layout: Approximately 50/50 grid on wider screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column: Display heading, substantial body text, attribution, and ghost button */}
            <div className="space-y-6">
              <div>
                <div className="inline-block px-3 py-1 bg-red-950/60 border border-red-800 text-red-400 text-xs font-bold uppercase tracking-[0.2em] mb-4">
                  <i className="fa-solid fa-wrench text-[#e20019] mr-1.5" aria-hidden="true" />
                  <span>Autoværksted & Bilsalg</span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white leading-tight">
                  Skriv til værkstedet
                </h2>
              </div>

              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                Udfyld formularen, så vender vi hurtigt og personligt tilbage til dig. Uanset om din
                bil trænger til det lovpligtige serviceeftersyn, fejlfinding, reparation af bremser,
                dækskift eller du overvejer at købe en af vores klargjorte brugte biler, står Steen og
                vores erfarne mekanikere klar med ærlig rådgivning.
              </p>

              {/* Smaller attribution / details */}
              <div className="pt-2 border-t border-white/10 space-y-2 text-xs sm:text-sm text-gray-300">
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-location-dot text-[#e20019] shrink-0" aria-hidden="true" />
                  <span>Mejerivej 12, 7250 Hejnsvig</span>
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#e20019] hover:underline font-semibold ml-2 inline-flex items-center gap-1"
                  >
                    <span>Find vej</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                  </a>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-phone text-[#e20019] shrink-0" aria-hidden="true" />
                  <a href={`tel:${phoneRaw}`} className="text-white hover:text-[#e20019] font-bold">
                    Tlf. {phone}
                  </a>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-400">Hverdage 7:30 – 16:00 (Fredag til 13:30)</span>
                </div>
                <div className="flex items-center gap-2.5 text-gray-400 text-xs">
                  <i className="fa-solid fa-envelope text-gray-500 shrink-0" aria-hidden="true" />
                  <a href={`mailto:${email}`} className="hover:text-white">
                    {email}
                  </a>
                  <span className="text-gray-600">•</span>
                  <span>CVR: 28566956</span>
                </div>
              </div>

              {/* Outlined primary action button (ghost button) */}
              <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-none border-2 border-white/80 hover:border-white text-white hover:bg-white/10 font-bold text-xs sm:text-sm uppercase tracking-wider transition-all text-center focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <i className="fa-solid fa-calendar-check text-[#e20019]" aria-hidden="true" />
                  <span>Book tid online</span>
                </a>
                <a
                  href={`tel:${phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-none text-gray-300 hover:text-white font-semibold text-xs sm:text-sm uppercase tracking-wider transition-colors text-center"
                >
                  <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                  <span>Eller ring {phone}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Contact form with bottom-border-only inputs and solid pill CTA button */}
            <div className="bg-[#1b2026]/90 border border-white/10 p-6 sm:p-10 shadow-2xl backdrop-blur-sm">
              <div className="mb-6">
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wide text-white">
                  Send en forespørgsel
                </h3>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  Udfyld dine oplysninger nedenfor. Vi besvarer altid inden for 24 timer på hverdage.
                </p>
              </div>

              <ContactForm bookingUrl={bookingUrl} variant="dark-minimal" />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
